import { customRangeFormat } from "@similarweb/pro-logic-components";
import { ESecondaryItemType, SecondaryItem } from "@similarweb/ui-components";

import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { applyNavItemPermissions } from "app/pages/workspace/common/workspacesUtils";
import { BasicDurations } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { INavItem } from "../../../types";

const ignoreSearchKeywordsParams = {
    /* eslint-disable @typescript-eslint/naming-convention */
    IncludeOrganic: null,
    IncludePaid: null,
    IncludeBranded: null,
    IncludeNoneBranded: null,
    IncludeNewKeywords: null,
    IncludeTrendingKeywords: null,
    IncludeQuestions: null,
    limits: null,
    BooleanSearchTerms: null,
    ExcludeUrls: null,
    IncludeUrls: null,
    IncludeTerms: null,
    ExcludeTerms: null,
    /* eslint-enable @typescript-eslint/naming-convention */
};

export const keywordResearchNavObj = (): { navList: INavItem[] } => {
    return {
        navList: [
            {
                title: "keywordresearch.monitorkeywords",
                name: "monitor_keywords",
                state: "digitalsuite_monitorkeywords_home",
                lockIcon: isLocked(swSettings.components.FindKeywordsHome),
                hidden: isHidden(swSettings.components.FindKeywordsHome),
                itemComponent: SecondaryItem,
                itemType: ESecondaryItemType.Standalone,
                itemComponentProps: {
                    itemIconName: "keyword-group",
                },
                subItems: [
                    {
                        title: "keywordresearch.findkeywords.byindustry",
                        name: "findkeywords_byindustry",
                        state: "digitalsuite_monitorkeywords",
                        lockIcon: false, // menu item is hidden by parent anyway
                    },
                ],
            },
            {
                title: "keywordresearch.keywordExplorer.title",
                name: "keyword_explorer",
                disableSelectedMode: true,
                subItems: [
                    {
                        title: "keywordresearch.KeywordGenerator",
                        name: "keywordAnalysis-overview",
                        state: "digitalsuite_findkeywords_keywordGeneratorTool",
                        lockIcon: isLocked(swSettings.components.KeywordAnalysisHome),
                        hidden: isHidden(swSettings.components.KeywordAnalysisHome),
                        overrideParams: {
                            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                        },
                    },
                    {
                        title: "digitalsuite.keywordresearch.overview.2",
                        name: "keywordAnalysisOverviewV2",
                        state: "organicsearch_keywordAnalysis_overview_2",
                        hidden: isHidden(swSettings.components.KeywordAnalysisV2Overview),
                        lockIcon: isLocked(swSettings.components.KeywordAnalysisV2Overview),
                    },
                    {
                        title: "keywordresearch.topPlayers",
                        name: "keywordAnalysis-topPlayers",
                        state: "organicsearch_keywordAnalysis_2",
                        hidden: isHidden(swSettings.components.KeywordAnalysisV2Organic),
                        lockIcon: isLocked(swSettings.components.KeywordAnalysisV2Organic),
                        overrideParams: {
                            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                        },
                    },
                    {
                        title: "trafficanalysis.2.page.title",
                        name: "keywordAnalysis-trafficAnalysis",
                        state: "organicsearch_trafficAnalysis_2",
                        lockIcon: isLocked(swSettings.components.KeywordAnalysisV2Overview),
                        hidden: isHidden(swSettings.components.KeywordAnalysisV2Overview),
                    },
                    {
                        title: "digitalsuite.keywordAnalysis.searchads.unify.page.title",
                        name: "keywordAnalysis-ads",
                        state: "paidsearch_keywordAnalysis_serp_ads",
                        lockIcon: isLocked(swSettings.components.PaidKwSearchTab),
                        hidden: isHidden(swSettings.components.PaidKwSearchTab),
                        defaultQueryParams: {
                            webSource: devicesTypes.DESKTOP,
                        },
                    },
                    {
                        title: "competitiveanalysis.keywordresearch.keywordanalysis.serpsnapshot",
                        name: "keywordanalysis.serpsnapshot",
                        state: "digitalsuite_keywordAnalysis_serpSnapshot",
                        lockIcon: isLocked(swSettings.components.SerpSnapshot),
                        hidden: isHidden(swSettings.components.SerpSnapshot),
                    },
                ],
            },
            {
                title: "keywordresearch.siteExplorer.title",
                name: "site_explorer",
                disableSelectedMode: true,
                subItems: [
                    {
                        title: "analysis.seo.overview.title",
                        name: "seoOverview",
                        state: "organicsearch_seo_overview",
                        options: {
                            isVirtualSupported: true,
                            isUSStatesSupported: true,
                        },
                        defaultQueryParams: {
                            ...ignoreSearchKeywordsParams,
                            IncludePaid: false,
                            IncludeOrganic: true,
                        },
                        lockIcon: isLocked(swSettings.components.SEOOverview),
                        hidden: isHidden(swSettings.components.SEOOverview),
                    },
                    {
                        title: "analysis.topics.title",
                        name: "topics",
                        state: "search_website_topics",
                        options: {
                            isVirtualSupported: true,
                            isUSStatesSupported: true,
                        },
                        defaultQueryParams: {
                            webSource: devicesTypes.TOTAL,
                        },
                        lockIcon: isLocked(swSettings.components.WebsiteTopics),
                        hidden: isHidden(swSettings.components.WebsiteTopics),
                    },
                    {
                        title: "analysis.search.keywords.title",
                        name: "searchKeywords",
                        state: "organicsearch_websites_v2_traffic_keywords",
                        options: {
                            isVirtualSupported: true,
                            isUSStatesSupported: true,
                        },
                        defaultQueryParams: {
                            ...ignoreSearchKeywordsParams,
                            IncludePaid: false,
                            IncludeOrganic: true,
                        },
                        lockIcon: isLocked(swSettings.components.WsWebSearchKeywords),
                        hidden: isHidden(swSettings.components.WsWebSearchKeywords),
                    },
                    {
                        title: "digitalsuite.analysis.search-traffic.landing.pages.v2.title",
                        name: "organicLandingPagesV2",
                        state: "organicsearch_website_landingpages_v2",
                        options: {
                            isVirtualSupported: false,
                            isUSStatesSupported: false,
                        },
                        hidden: isHidden(swSettings.components.KeywordAnalysisV2Organic),
                        lockIcon: isLocked(swSettings.components.KeywordAnalysisV2Organic),
                    },
                    {
                        title: "analysis.search.website.competitors.title",
                        state: "organicsearch_websites-competitorsKeywords",
                        name: "competitorsOrganicKeywords",
                        options: {
                            isVirtualSupported: false,
                            isUSStatesSupported: true,
                        },
                        lockIcon: isLocked(swSettings.components.WsWebOrganicSearchCompetitors),
                        hidden: isHidden(swSettings.components.WsWebOrganicSearchCompetitors),
                        overrideParams: {
                            duration: BasicDurations.LAST_THREE_MONTHS,
                            category: null,
                        },
                    },
                    /// V1 - will be removed after releasing V2
                    /// https://similarweb-rnd.atlassian.net/browse/DMI-11111

                    {
                        title: "analysis.search.ranking.distribution.title",
                        name: "searchRankingDistribution",
                        state: "organicsearch_website_search_ranking_distribution_v2",
                        options: {
                            isVirtualSupported: true,
                            isUSStatesSupported: true,
                        },
                        overrideParams: {
                            ...ignoreSearchKeywordsParams,
                            duration: swSettings.components.RankingDistributionV2.endDate
                                .startOf("month")
                                .format(`${customRangeFormat}-${customRangeFormat}`),
                        },
                        lockIcon: isLocked(swSettings.components.RankingDistributionV2),
                        hidden: isHidden(swSettings.components?.RankingDistributionV2),
                    },

                    {
                        title: "analysis.search.ads.title",
                        name: "plaResearch",
                        state: "paidsearch_website_ads",
                        defaultQueryParams: ignoreSearchKeywordsParams,
                        lockIcon: isLocked(swSettings.components.PaidSearchPages),
                        hidden: isHidden(swSettings.components.PaidSearchPages),
                        options: {
                            isVirtualSupported: true,
                            isUSStatesSupported: true,
                        },
                    },
                ],
            },
        ].map(applyNavItemPermissions) as INavItem[],
    };
};
